import React from "react";
import { motion } from "framer-motion";
import { Anchor, Briefcase, HeartPulse, ShieldCheck } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";
import { useNarrationCue } from "@/components/clientDemo/tour/TourCuePointer";

// Source: Exchange platform /four-pillars ("Commitment to Career · Streets to Stability").
const PILLARS = [
  { id: "inner", Icon: HeartPulse, kicker: "Foundation of Self", name: "Inner Strength & Case Management",
    quote: "You cannot pour from an empty cup. We fill it first.",
    points: ["Weekly STS Road-Map homework with trauma-informed prompts", "Emotional identity rebuilt through milestone reviews and accountability partners", "Compliance tracking on housing, sobriety and family reunification"] },
  { id: "vocation", Icon: Briefcase, kicker: "The Workforce Bridge", name: "Vocation & Practical Life Skills",
    quote: "Dignity is earned in the doing. Skill is the shield.",
    points: ["Apprenticeships across Hospitality, Logistics, Skilled Trades and Healthcare", "Financial-literacy labs: budgeting, credit repair, taxes, savings ladders", "Life skills: cooking, transportation, healthcare navigation, civics"] },
  { id: "spiritual", Icon: Anchor, kicker: "Anchoring the Heart", name: "Christ-Centered Spiritual Counseling",
    quote: "The anchor is not what floats you — it is what holds you.",
    points: ["Voluntary weekly pastoral counseling with credentialed chaplains", "Small-group scripture study on identity, forgiveness and purpose", "Prayer walks, worship nights and communion meals"] },
  { id: "touchpoint", Icon: ShieldCheck, kicker: "Daily Accountability", name: "The Daily Touchpoint & Residential Check-In",
    quote: "Accountability is love, made visible every single day.",
    points: ["Unit Ambassador daily walk-through with same-day escalation", "Sanctuary inspections tied to merit ledgers and restitution", "24-month Road-Map milestones surfacing every week"] },
];
export const PILLAR_CUES = PILLARS.map((p, i) => ({ phrase: `pillar ${["one", "two", "three", "four"][i]}`, target: `exchange-pillar-${p.id}` }));

function PillarCard({ p, i, shown, active }) {
  return (
    <article data-testid={`exchange-pillar-${p.id}`}
      className={`relative rounded-2xl border p-5 bg-[#120C1C]/80 overflow-hidden transition-[border-color,box-shadow,opacity] duration-500 ${active ? "border-[#D4AF37]/80 shadow-[0_0_34px_rgba(212,175,55,0.22)]" : "border-[#D4AF37]/20"} ${shown ? "opacity-100" : "opacity-40"}`}>
      <span aria-hidden className="absolute -right-2 -top-6 font-black text-[110px] leading-none text-[#D4AF37]/[0.07] select-none">0{i + 1}</span>
      <div className="relative flex items-center gap-3">
        <p.Icon className="w-6 h-6 text-[#F4D88A]" strokeWidth={1.5} />
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227]">Pillar 0{i + 1} · {p.kicker}</div>
          <h3 className="text-white font-bold leading-snug">{p.name}</h3>
        </div>
      </div>
      <p className="relative mt-3 text-lg md:text-xl italic font-light leading-snug text-[#FAFAF7]">“{p.quote}”</p>
      <ul className="relative mt-3 space-y-1.5">
        {p.points.map((t, j) => (
          <motion.li key={`${t}-${shown}`} initial={shown ? { opacity: 0, x: -8 } : false} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + j * 0.5 }}
            className="flex items-start gap-2 text-xs text-slate-300">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />{t}
          </motion.li>
        ))}
      </ul>
    </article>
  );
}

// Featured beat — the Four Pillars of Commitment to Career, revealed as the narration names each one.
export default function ExchangePillarsSection() {
  const { isActive, current } = useGuidedTour();
  const live = isActive && current?.id === "pillars";
  const cue = useNarrationCue(PILLAR_CUES.map((c) => c.phrase), live);
  const reached = live ? cue : 3;
  return (
    <section data-testid="exchange-pillars-section" className="mt-8 rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#120C1C] to-[#0A0612] p-6 md:p-8">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">// 05 · Commitment to Career · Streets to Stability</div>
        <h2 className="mt-1 text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-white to-[#F4D88A] bg-clip-text text-transparent">The Four Pillars</h2>
        <p className="mt-1 text-sm text-slate-400">A 24-month, whole-person transformation built on an unbreakable foundation.</p>
      </div>
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PILLARS.map((p, i) => <PillarCard key={p.id} p={p} i={i} shown={reached >= i} active={live && cue === i} />)}
          </div>
          <div className="mt-5" data-testid="exchange-pillars-roadmap">
            <div className="flex justify-between font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500"><span>Month 0</span><span>24-month Road-Map</span><span>Month 24</span></div>
            <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-[#C9A227] to-[#F4D88A]" animate={{ width: `${((reached + 1) / 4) * 100}%` }} transition={{ duration: 0.9 }} />
            </div>
          </div>
        </div>
        <aside data-testid="exchange-pillars-dock" className="hidden xl:flex w-[440px] shrink-0 rounded-2xl border border-dashed border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] p-6 flex-col justify-center">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A227]">The whole person</div>
          <p className="mt-3 text-sm italic text-slate-300">Anchoring the heart in Christ, rebuilding emotional identity, mastering professional and practical life skills, and maintaining daily accountability.</p>
        </aside>
      </div>
    </section>
  );
}
